import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import { queryKeys } from "@/queryKeys";
import { useMockAuth, mockBorrowers, mockDecisions } from "@/mocks/mockData";

export interface DashboardStats {
  totalBorrowers: number;
  pendingDecisions: number;
  approvedDecisions: number;
  recentBorrowers: Array<{
    id: number;
    company_name: string;
    owner_name?: string;
    processing_status?: string;
  }>;
}

async function fetchDashboardStats(): Promise<DashboardStats> {
  if (useMockAuth) {
    const approved = mockDecisions.filter(
      (d: { decision_status: string }) => d.decision_status === "approved"
    ).length;
    const pending = mockDecisions.filter(
      (d: { decision_status: string }) =>
        d.decision_status === "processing" || d.decision_status === "needs_review"
    ).length;
    return {
      totalBorrowers: mockBorrowers.length,
      pendingDecisions: pending,
      approvedDecisions: approved,
      recentBorrowers: mockBorrowers.slice(0, 5) as DashboardStats["recentBorrowers"],
    };
  }
  const [borrowersRes, decisionsRes] = await Promise.all([
    api.get("/borrowers/?page_size=5"),
    api.get("/decisions/statistics/"),
  ]);
  return {
    totalBorrowers: borrowersRes.data.count ?? 0,
    pendingDecisions: decisionsRes.data.pending ?? 0,
    approvedDecisions: decisionsRes.data.approved ?? 0,
    recentBorrowers: (borrowersRes.data.results ?? []) as DashboardStats["recentBorrowers"],
  };
}

export function useDashboardStatsQuery() {
  return useQuery({
    queryKey: queryKeys.dashboardStats(),
    queryFn: fetchDashboardStats,
    staleTime: 60_000,
  });
}
